import { CopyIdButton } from '@/components/shared';
import { useMediaQuery } from '@/lib/use-media-query';
import type { BackofficeTag } from '../types';
import { TagStatusChip } from './TagStatusChip';

interface TagsTableHeaderProps {
  loading: boolean;
  total: number;
}

interface TagsTableProps {
  tags: BackofficeTag[];
  onEditTag: (tag: BackofficeTag) => void;
}

export function TagsTableHeader({ loading, total }: TagsTableHeaderProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-neutral-50 px-5 py-4">
      <div className="text-small font-medium text-neutral-500">
        {loading ? 'Loading tags' : `${total} tags`}
      </div>
    </div>
  );
}

export function TagsTable({ tags, onEditTag }: TagsTableProps) {
  const isMobile = useMediaQuery('(max-width: 767px)');

  if (isMobile) {
    return (
      <div className="divide-y divide-neutral-100">
        {tags.map((tag) => (
          <article
            key={tag.id}
            role="button"
            tabIndex={0}
            className="cursor-pointer px-4 py-4 hover:bg-backoffice-50"
            onClick={() => onEditTag(tag)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onEditTag(tag);
              }
            }}
          >
            <div className="flex min-w-0 items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="truncate text-body font-semibold text-neutral-900">{tag.name}</h2>
                <div
                  className="mt-1 flex items-center gap-1 font-mono text-small text-neutral-500"
                  onClick={(event) => event.stopPropagation()}
                >
                  <span>{tag.id}</span>
                  <CopyIdButton value={tag.id} label="Copy tag ID" />
                </div>
              </div>
              <TagStatusChip status={tag.status} />
            </div>
            <p className="mt-3 text-small text-neutral-600">
              {tag.description || 'No description'}
            </p>
          </article>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] border-collapse text-left">
        <thead className="border-b border-neutral-200 bg-white text-small text-neutral-500">
          <tr>
            <th scope="col" className="min-w-[220px] px-4 py-3 font-semibold text-neutral-600">
              Name
            </th>
            <th scope="col" className="min-w-[220px] px-4 py-3 font-semibold text-neutral-600">
              ID
            </th>
            <th scope="col" className="min-w-[300px] px-4 py-3 font-semibold text-neutral-600">
              Description
            </th>
            <th scope="col" className="min-w-[120px] px-4 py-3 font-semibold text-neutral-600">
              Status
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100 text-body">
          {tags.map((tag) => (
            <tr
              key={tag.id}
              className="cursor-pointer hover:bg-backoffice-50"
              onClick={() => onEditTag(tag)}
            >
              <td className="px-4 py-3 font-semibold text-neutral-900">{tag.name}</td>
              <td className="px-4 py-3">
                <div
                  className="flex items-center gap-2 font-mono text-small text-neutral-700"
                  onClick={(event) => event.stopPropagation()}
                >
                  <span>{tag.id}</span>
                  <CopyIdButton value={tag.id} label="Copy tag ID" />
                </div>
              </td>
              <td className="px-4 py-3 text-neutral-700">{tag.description || 'No description'}</td>
              <td className="px-4 py-3">
                <TagStatusChip status={tag.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
